import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { UserGroupIcon } from '@heroicons/vue/solid';

import store from '@/store';
import { useFilterStore, useUiStore } from '@/stores';
import UserLabelService from '@/services/api/UserLabelService';
import { toast, toastServiceError } from '@/services/core/notification';

import {
    FilterItem,
    Permission,
    SortMenuItem,
    UserLabelActions,
    UserLabelFilter,
    UserLabelGetters,
    UserLabelModel,
    UserLabelServiceGetAllParams,
} from '@/types';
import {
    UserLabelListItem,
    UserLabelListSort,
    UserLabelListFilters,
} from '@/views/pages/user-label/components';

const perPage = 10;


export default /*@__PURE__*/_defineComponent({
  __name: 'UserLabelPage',
  setup(__props, { expose: __expose }) {
  __expose();

const route = useRoute();
const router = useRouter();

const filterStore = useFilterStore();
const uiStore = useUiStore();

const userLabels = ref<UserLabelModel[]>([]);
const count = ref(0);
const search = computed({
    get: () => store.getters[UserLabelGetters.SEARCH],
    set: (value: string) => store.dispatch(UserLabelActions.SET_SEARCH, value),
});

const sortMenuSelected = computed<SortMenuItem[]>({
    get: () => store.getters[UserLabelGetters.SORT],
    set: (value: SortMenuItem[]) => store.dispatch(UserLabelActions.SET_SORT, value),
});

const level = computed(() => filterStore.level);

const currentPage = computed({
    get: () => store.getters[UserLabelGetters.PAGE],
    set: (value: number) => store.dispatch(UserLabelActions.SET_PAGE, value),
});

const filter: UserLabelFilter = store.getters[UserLabelGetters.FILTER];

const activeFilters = computed<FilterItem[]>(() => store.getters[UserLabelGetters.ACTIVE_FILTERS]);

const orderBy = computed(() => sortMenuSelected.value.map((s) => s.direction + s.key).join(','));

watch([currentPage], () => {
    fetchUserLabels();
});

watch([level, search, filter, orderBy], () => {
    if (currentPage.value === 1) fetchUserLabels();
    else currentPage.value = 1;
});

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getParams = (): UserLabelServiceGetAllParams => ({
    page: currentPage.value,
    per_page: perPage,
    order_by: orderBy.value,
    is_active: filter.isActive !== null ? (filter.isActive.value as boolean) : undefined,
    search: search.value,
});

const fetchUserLabels = async () => {
    setLoading(true);
    try {
        const { data, headers } = await UserLabelService.getAll<UserLabelModel[]>({
            params: getParams(),
        });
        userLabels.value = data;
        count.value = parseInt(headers['x-pagination-count']);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const setIsActive = async (item: UserLabelModel, isActive: boolean) => {
    setLoading(true);
    try {
        const { data } = await (
            isActive
                ? (id: number) => UserLabelService.activate(id)
                : (id: number) => UserLabelService.inactivate(id)
        )(item.id);
        toast.success(data.message);
        await fetchUserLabels();
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const handleRemoveSortClick = (item: SortMenuItem) => {
    const index = sortMenuSelected.value.indexOf(item);
    if (index > -1) sortMenuSelected.value.splice(index, 1);
};

const handleRemoveFilterClick = (item: FilterItem) =>
    store.dispatch(UserLabelActions.CLEAR_FILTER, item);

const handleActivateClick = (item: UserLabelModel) => setIsActive(item, true);

const handleInactivateClick = (item: UserLabelModel) => setIsActive(item, false);

onMounted(() => {
    if (route.query.clearStateOnSetup) {
        search.value = '';
        sortMenuSelected.value = [];
        currentPage.value = 1;
        router.replace({ query: {} });
    }

    fetchUserLabels();
});

const __returned__ = { route, router, filterStore, uiStore, userLabels, count, perPage, search, sortMenuSelected, level, currentPage, filter, activeFilters, orderBy, setLoading, getParams, fetchUserLabels, setIsActive, handleRemoveSortClick, handleRemoveFilterClick, handleActivateClick, handleInactivateClick, get UserGroupIcon() { return UserGroupIcon }, get Permission() { return Permission }, get UserLabelListItem() { return UserLabelListItem }, get UserLabelListSort() { return UserLabelListSort }, get UserLabelListFilters() { return UserLabelListFilters } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})